import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { GeoJSONSource, Map as MlMap } from "maplibre-gl";
import { AttributionControl, LngLatBounds, Map, Marker, NavigationControl, Popup, ScaleControl } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { SearchMarker, type SearchResult } from "./searchMarker";
import { fr } from "../i18n/fr";
import { buildLayers, SOURCES, type LayerDef, type LayerGroup, type SourceName } from "./layers";
import { applyBasemap, arrowImage, loadBaseStyle } from "./style";
import { basemapAt, outsideAt, type PackArea } from "./packs";
import type { Styles } from "./symbology";

export type FC = GeoJSON.FeatureCollection;
export const EMPTY: FC = { type: "FeatureCollection", features: [] };

export interface LatLonPt { lat: number; lon: number }

export interface RouteMapHandle {
  fit(points: [number, number][]): void;
  flyTo(p: LatLonPt, zoom?: number): void;
  /** marker of a search result with its popup, null removes it (C2) */
  showSearchResult(r: SearchResult | null): void;
}

interface Props {
  data: Partial<Record<SourceName, FC>>;
  visible: Record<LayerGroup, boolean>;
  symbology: Styles;
  /** basemap of the event's regional pack */
  basemapUrl?: string | null;
  /** bounding box of the pack [minLon, minLat, maxLon, maxLat]: outside it the basemap is empty (SW-DP-04) */
  bbox?: number[] | null;
  /** packs of the manifest: without [basemapUrl], the basemap of the pack under the view centre is shown */
  packs?: PackArea[];
  origin: LatLonPt | null;
  destination: LatLonPt | null;
  onMapClick(p: LatLonPt): void;
  onOriginMoved(p: LatLonPt): void;
  onDestinationMoved(p: LatLonPt): void;
}

function pin(label: string, color: string): HTMLElement {
  const el = document.createElement("div");
  el.className = "pin";
  el.style.background = color;
  el.textContent = label;
  return el;
}

const EFFECT_LABEL: Record<string, string> = fr.legend;

export const RouteMap = forwardRef<RouteMapHandle, Props>(function RouteMap(props, ref) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<MlMap | null>(null);
  const [ready, setReady] = useState(false);
  const [outside, setOutside] = useState(false);
  const markers = useRef<{ o: Marker | null; d: Marker | null }>({ o: null, d: null });
  const layers = useRef<LayerDef[]>([]);
  // extent asked before the map exists (the basemap depends on the event's pack), applied at creation
  const pendingFit = useRef<LngLatBounds | null>(null);
  const search = useRef(new SearchMarker());
  const cb = useRef(props);
  cb.current = props;

  useImperativeHandle(ref, () => ({
    fit(points) {
      if (points.length === 0) return;
      const b = new LngLatBounds(points[0], points[0]);
      for (const p of points) b.extend(p);
      if (!map.current) { pendingFit.current = b; return; }
      map.current.fitBounds(b, { padding: 60, maxZoom: 17, duration: 400 });
    },
    flyTo(p, zoom = 17) {
      map.current?.flyTo({ center: [p.lon, p.lat], zoom, duration: 400 });
    },
    showSearchResult(r) { if (map.current) search.current.show(map.current, r); },
  }));

  /** basemap and "outside the packs" notice for the current view and pack (the pack may arrive after the map) */
  const loaded = useRef(false);
  const refresh = (m: MlMap) => {
    const c = m.getCenter();
    setOutside(outsideAt(cb.current.bbox, cb.current.packs, c.lng, c.lat));
    if (loaded.current) applyBasemap(m, basemapAt(cb.current.basemapUrl, cb.current.packs, c.lng, c.lat));
  };

  useEffect(() => {
    let disposed = false;
    loadBaseStyle(cb.current.basemapUrl).then((style) => {
      if (disposed || !container.current) return;
      const m = new Map({
        container: container.current, style, center: [3.523, 50.36], zoom: 13, maxZoom: 19,
        ...(pendingFit.current ? { bounds: pendingFit.current, fitBoundsOptions: { padding: 60, maxZoom: 17 } } : {}),
        attributionControl: false,
      });
      m.addControl(new AttributionControl({ compact: false, customAttribution: fr.attribution }));
      m.addControl(new NavigationControl({ showCompass: true }), "top-right");
      m.addControl(new ScaleControl({ unit: "metric" }), "bottom-left");
      m.on("load", () => {
        const S = cb.current.symbology;
        m.addImage("arrow-double-green", arrowImage(S.ONEWAY_LIFTED.color, true));
        m.addImage("arrow-violet", arrowImage(S.ONEWAY_REVERSED.color, false));
        m.addImage("arrow-course", arrowImage(S.COURSE.color, false));
        for (const s of SOURCES) m.addSource(s, { type: "geojson", data: EMPTY });
        layers.current = buildLayers(S);
        for (const l of layers.current) m.addLayer(l.layer);
        // POIs in the colour of their category, as in the editor and the legend
        m.setPaintProperty("poi", "circle-color", ["coalesce", ["get", "color"], S.POI.color]);
        m.setPaintProperty("poi-label", "text-color", ["coalesce", ["get", "color"], S.POI.color]);
        loaded.current = true;
        refresh(m);
        setReady(true);
      });
      m.on("click", (e) => {
        // a click on an effect shows its explanation instead of placing a point
        const hits = m.queryRenderedFeatures(e.point, { layers: ["fx-cut", "fx-closed", "fx-public-closed", "fx-emergency", "fx-reversed", "fx-lifted", "fx-course", "fx-course-upcoming", "fx-soft", "fx-enclosed", "fx-inaccessible", "poi"] });
        if (hits.length > 0) {
          const p = hits[0].properties as Record<string, unknown>;
          const ids = typeof p.restrictionIds === "string" ? (JSON.parse(p.restrictionIds) as string[]) : [];
          const title = p.name ? String(p.name) : EFFECT_LABEL[String(p.effect)] ?? String(p.effect);
          const html = `<strong>${escapeHtml(title)}</strong>` +
            (p.streetName ? `<br>${escapeHtml(String(p.streetName))}` : "") +
            (p.oldDirection ? `<br>${escapeHtml(fr.oldDirection[String(p.oldDirection)] ?? "")}` : "") +
            (ids.length ? `<br><span class="muted">${escapeHtml(ids.join(", "))}</span>` : "");
          new Popup({ closeButton: true, maxWidth: "260px" }).setLngLat(e.lngLat).setHTML(html).addTo(m);
          return;
        }
        cb.current.onMapClick({ lat: e.lngLat.lat, lon: e.lngLat.lng });
      });
      for (const id of ["fx-cut", "fx-closed", "fx-public-closed", "fx-emergency", "fx-reversed", "fx-lifted", "fx-course", "fx-course-upcoming", "poi"]) {
        m.on("mouseenter", id, () => (m.getCanvas().style.cursor = "pointer"));
        m.on("mouseleave", id, () => (m.getCanvas().style.cursor = ""));
      }
      // outside the pack the basemap has no tiles: grid and notice (SW-DP-04)
      m.on("moveend", () => refresh(m));
      map.current = m;
    });
    return () => {
      disposed = true; loaded.current = false;
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // pack known or changed after the map was created: basemap and notice follow (event without zone or object, admin page)
  useEffect(() => {
    if (ready && map.current) refresh(map.current);
  }, [ready, props.basemapUrl, props.bbox, props.packs]); // eslint-disable-line react-hooks/exhaustive-deps

  // sources
  useEffect(() => {
    if (!ready || !map.current) return;
    for (const s of SOURCES) (map.current.getSource(s) as GeoJSONSource).setData(props.data[s] ?? EMPTY);
  }, [ready, props.data]);

  // layer groups
  useEffect(() => {
    if (!ready || !map.current) return;
    for (const l of layers.current) map.current.setLayoutProperty(l.layer.id, "visibility", props.visible[l.group] ? "visible" : "none");
  }, [ready, props.visible]);

  // origin and destination markers, draggable (SW-RT-02)
  useEffect(() => {
    if (!ready || !map.current) return;
    const m = map.current;
    const sync = (key: "o" | "d", p: LatLonPt | null, label: string, color: string, moved: (p: LatLonPt) => void) => {
      const cur = markers.current[key];
      if (!p) { cur?.remove(); markers.current[key] = null; return; }
      if (cur) { cur.setLngLat([p.lon, p.lat]); return; }
      const mk = new Marker({ element: pin(label, color), draggable: true, anchor: "bottom" }).setLngLat([p.lon, p.lat]).addTo(m);
      mk.on("dragend", () => { const ll = mk.getLngLat(); moved({ lat: ll.lat, lon: ll.lng }); });
      markers.current[key] = mk;
    };
    sync("o", props.origin, "D", "#2b8a3e", (p) => cb.current.onOriginMoved(p));
    sync("d", props.destination, "A", "#c92a2a", (p) => cb.current.onDestinationMoved(p));
  }, [ready, props.origin, props.destination]);

  return (
    <div className={"map-wrap" + (outside ? " outside" : "")}>
      <div ref={container} className="map" />
      {outside && <div className="map-notice">{fr.workspace.outsidePacks}</div>}
    </div>
  );
});

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
